import React, { useState, useEffect, useCallback } from 'react';
import { Users, Clock, CheckCircle, XCircle, AlertCircle, LogOut, RefreshCw, UserCheck, UserX, UserMinus } from 'react-feather';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAllUsers, updateUserStatus, getUserStats, UserWithDetails } from '../../services/adminService';
import './AdminDashboard.css';

type StatusFilter = 'all' | 'pending' | 'active' | 'suspended' | 'rejected';

export const AdminDashboard: React.FC = () => {
  const { signOut } = useAuth();
  const [users, setUsers] = useState<UserWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [stats, setStats] = useState({ total: 0, pending: 0, active: 0, suspended: 0, rejected: 0 });
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    const [usersResult, statsResult] = await Promise.all([
      fetchAllUsers(),
      getUserStats(),
    ]);

    if (usersResult.error) {
      setError(usersResult.error);
    } else {
      setUsers(usersResult.users);
    }

    if (!statsResult.error) {
      setStats(statsResult);
    }

    setLoading(false);
    setIsRefreshing(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStatusChange = async (userId: string, newStatus: 'pending' | 'active' | 'suspended' | 'rejected') => {
    setUpdatingUserId(userId);
    const result = await updateUserStatus(userId, newStatus);
    
    if (result.success) {
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, account_status: newStatus } : u
      ));
      const statsResult = await getUserStats();
      if (!statsResult.error) {
        setStats(statsResult);
      }
    } else {
      alert(`Failed to update user: ${result.error}`);
    }
    
    setUpdatingUserId(null);
  };

  const filteredUsers = users.filter(user => {
    if (statusFilter === 'all') return true;
    return user.account_status === statusFilter;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="status-badge pending"><Clock size={8} /> Pending</span>;
      case 'active':
        return <span className="status-badge active"><CheckCircle size={8} /> Active</span>;
      case 'suspended':
        return <span className="status-badge suspended"><AlertCircle size={8} /> Suspend</span>;
      case 'rejected':
        return <span className="status-badge rejected"><XCircle size={8} /> Rejected</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
    });
  };

  const getAvatarColor = (email: string | null) => {
    const colors = [
      '#6366f1',
      '#ec4899',
      '#f59e0b',
      '#10b981',
      '#3b82f6',
      '#8b5cf6',
    ];
    const index = (email?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <h1 onClick={() => window.location.href = '/'} style={{ cursor: 'pointer' }}>TowApp</h1>
          <span className="admin-badge">Admin</span>
        </div>
        <div className="admin-header-right">
          <button className="icon-btn" onClick={() => loadData(true)} disabled={loading || isRefreshing} title="Refresh">
            <RefreshCw size={16} className={isRefreshing ? 'spinning' : ''} />
          </button>
          <button className="signout-btn" onClick={signOut}>
            <LogOut size={14} />
            <span>Sign Out</span>
          </button>
        </div>
      </header>

      <div className="admin-content">
        {/* Stats Cards */}
        <div className="stats-grid">
          <div 
            className={`stat-card ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            <div className="stat-icon total">
              <Users size={16} />
            </div>
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
          
          <div 
            className={`stat-card ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            <div className="stat-icon pending">
              <Clock size={16} />
            </div>
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">Pending</span>
          </div>
          
          <div 
            className={`stat-card ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => setStatusFilter('active')}
          >
            <div className="stat-icon active">
              <CheckCircle size={16} />
            </div>
            <span className="stat-value">{stats.active}</span>
            <span className="stat-label">Active</span>
          </div>
          
          <div 
            className={`stat-card ${statusFilter === 'suspended' ? 'active' : ''}`}
            onClick={() => setStatusFilter('suspended')}
          >
            <div className="stat-icon suspended">
              <AlertCircle size={16} />
            </div>
            <span className="stat-value">{stats.suspended}</span>
            <span className="stat-label">Suspend</span>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All <span className="badge">{stats.total}</span>
          </button>
          <button 
            className={`filter-tab ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            Pending <span className="badge">{stats.pending}</span>
          </button>
          <button 
            className={`filter-tab ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => setStatusFilter('active')}
          >
            Active <span className="badge">{stats.active}</span>
          </button>
          <button 
            className={`filter-tab ${statusFilter === 'suspended' ? 'active' : ''}`}
            onClick={() => setStatusFilter('suspended')}
          >
            Suspended <span className="badge">{stats.suspended}</span>
          </button>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="loading-state">
            <RefreshCw size={28} className="spinning" />
            <p>Loading users...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <AlertCircle size={28} />
            <p>{error}</p>
            <button onClick={() => loadData()}>Try Again</button>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <p>No {statusFilter === 'all' ? '' : statusFilter + ' '}users found</p>
          </div>
        ) : (
          <div className="users-list">
            {filteredUsers.map(user => (
              <div key={user.id} className="user-card">
                <div className="user-row">
                  <div 
                    className="user-avatar"
                    style={{ background: getAvatarColor(user.email) }}
                  >
                    {user.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="user-meta">
                    <div className="user-name">{user.full_name || 'No Name'}</div>
                    <div className="user-email">{user.email}</div>
                    <div className="user-date">{formatDate(user.created_at)}</div>
                  </div>
                </div>
                <div className="user-status">
                  {getStatusBadge(user.account_status)}
                </div>
                
                <div className="user-actions">
                  {user.account_status === 'pending' && (
                    <>
                      <button 
                        className="action-btn approve"
                        onClick={() => handleStatusChange(user.id, 'active')}
                        disabled={updatingUserId === user.id}
                      >
                        <UserCheck size={12} />
                        Approve
                      </button>
                      <button 
                        className="action-btn reject"
                        onClick={() => handleStatusChange(user.id, 'rejected')}
                        disabled={updatingUserId === user.id}
                      >
                        <UserX size={12} />
                        Reject
                      </button>
                    </>
                  )}
                  
                  {user.account_status === 'active' && (
                    <button 
                      className="action-btn suspend"
                      onClick={() => handleStatusChange(user.id, 'suspended')}
                      disabled={updatingUserId === user.id}
                    >
                      <UserMinus size={12} />
                      Suspend
                    </button>
                  )}
                  
                  {user.account_status === 'suspended' && (
                    <button 
                      className="action-btn reactivate"
                      onClick={() => handleStatusChange(user.id, 'active')}
                      disabled={updatingUserId === user.id}
                    >
                      <UserCheck size={12} />
                      Activate
                    </button>
                  )}
                  
                  {user.account_status === 'rejected' && (
                    <button 
                      className="action-btn reactivate"
                      onClick={() => handleStatusChange(user.id, 'active')}
                      disabled={updatingUserId === user.id}
                    >
                      <UserCheck size={12} />
                      Approve
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


